import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "axios";
import { WizardWrapper, WizardButton } from "./AddTalentProfile.style";
import ResumeUpload from "./ResumeUpload";
import BasicDetails from "./BasicDetails";
import ProfessionalDetails from "./ProfessionalDetails";
import AvailabilityDocuments from "./AvailabilityDocuments";
import { API_CONST } from "../../../const";

const STEPS = [
  { key: "resume",   title: "Resume Upload",       description: "Upload & auto-fill with AI" },
  { key: "basic",    title: "Basic Details",        description: "Personal info & rate" },
  { key: "professional", title: "Professional",    description: "Skills, experience & projects" },
  { key: "documents", title: "Availability & Docs", description: "Documents & availability" },
];

const AddTalentProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const submitToJobId = searchParams.get("submitToJob");
  const isEditMode = Boolean(editId);

  const [currentStep, setCurrentStep] = useState(isEditMode ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [partnerOrganization, setPartnerOrganization] = useState("");
  const [resumeFile, setResumeFile] = useState(null);

  const [formData, setFormData] = useState({
    partnerOrganization: "",
    name: "",
    role: "",
    email: "",
    phone: "",
    gender: undefined,
    dob: undefined,
    country: "India",
    location: "",
    rate: "",
    experienceYears: "",
    experienceMonths: "",
    university: "",
    graduationYear: undefined,
    primarySkills: [],
    secondarySkills: [],
    skills: [],
    summary: "",
    projects: [],
    availability: "",
    notice: "",
    resumeUrl: null,
    aadhaarUrl: null,
    passportUrl: null,
    panUrl: null,
    degreeUrl: null,
    aadhaarNumber: "",
    panNumber: "",
    passportNumber: "",
  });

  const basicValidatorRef = useRef(null);
  const professionalValidatorRef = useRef(null);
  const docsValidatorRef = useRef(null);

  useEffect(() => {
    const userOrg = localStorage.getItem("userOrganization");
    if (userOrg) {
      setPartnerOrganization(userOrg);
      setFormData((prev) => ({ ...prev, partnerOrganization: userOrg }));
      return;
    }
    try {
      const user = JSON.parse(
        localStorage.getItem("user") ||
        localStorage.getItem("userDetails") ||
        localStorage.getItem("userData") ||
        "{}"
      );
      const org = user.organization || user.partnerOrganization || user.org || "";
      if (org) {
        setPartnerOrganization(org);
        setFormData((prev) => ({ ...prev, partnerOrganization: org }));
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (isEditMode && editId) {
      fetchTalentProfile(editId);
    }
  }, [isEditMode, editId]);

  const fetchTalentProfile = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${API_CONST.GET_TALENT_PROFILE}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.profile) {
        const p = res.data.profile;
        setFormData((prev) => ({
          ...prev,
          ...p,
          rate: p.rate ?? p.monthlyRate ?? "",
          notice: p.notice ?? p.noticePeriod ?? "",
          dob: p.dob || undefined,
          gender: p.gender || undefined,
          university: p.university || "",
          graduationYear: p.graduationYear || undefined,
          primarySkills: p.primarySkills || [],
          secondarySkills: p.secondarySkills || [],
          projects: p.projects || [],
        }));
      }
    } catch {
      message.error("Failed to load talent profile");
    } finally {
      setLoading(false);
    }
  };

  const handleParsed = (parsed, file) => {
    setResumeFile(file);
    if (parsed) {
      const updates = {};
      if (parsed.name) updates.name = parsed.name;
      if (parsed.email) updates.email = parsed.email;
      if (parsed.phone) updates.phone = parsed.phone;
      if (parsed.gender) updates.gender = parsed.gender;
      if (parsed.dob) updates.dob = parsed.dob;
      if (parsed.summary) updates.summary = parsed.summary;
      if (parsed.university) updates.university = parsed.university;
      if (parsed.graduationYear) updates.graduationYear = parsed.graduationYear;
      if (parsed.experienceYears !== undefined) updates.experienceYears = String(parsed.experienceYears);
      if (parsed.experienceMonths !== undefined) updates.experienceMonths = String(parsed.experienceMonths);
      if (parsed.location?.country) updates.country = parsed.location.country;
      if (parsed.location?.city) updates.location = parsed.location.city;
      if (Array.isArray(parsed.primarySkills) && parsed.primarySkills.length > 0) {
        updates.primarySkills = parsed.primarySkills.slice(0, 3);
      }
      if (Array.isArray(parsed.secondarySkills)) {
        updates.secondarySkills = parsed.secondarySkills;
      }
      if (Array.isArray(parsed.projects) && parsed.projects.length > 0) {
        updates.projects = parsed.projects;
      }
      setFormData((prev) => ({ ...prev, ...updates }));
    }
    setCurrentStep(1);
  };

  const handleSkip = (file) => {
    if (file) setResumeFile(file);
    setCurrentStep(1);
  };

  const handleNext = async () => {
    if (currentStep === 1 && basicValidatorRef.current) {
      try {
        const values = await basicValidatorRef.current();
        const dobStr = values.dob && dayjs.isDayjs(values.dob) ? values.dob.format("YYYY-MM-DD") : values.dob;
        setFormData((prev) => ({ ...prev, ...values, dob: dobStr }));
        setCurrentStep(2);
      } catch {}
      return;
    }
    if (currentStep === 2 && professionalValidatorRef.current) {
      try {
        const values = await professionalValidatorRef.current();
        setFormData((prev) => ({ ...prev, ...values }));
        setCurrentStep(3);
      } catch {}
      return;
    }
  };

  const handleBack = () => {
    if (currentStep > (isEditMode ? 1 : 0)) setCurrentStep((s) => s - 1);
  };

  const handleCancel = () => {
    if (submitToJobId) navigate(`/partner/submit-profiles/${submitToJobId}`);
    else navigate("/partner/bench-pool");
  };

  const handleSubmit = async () => {
    if (!docsValidatorRef.current) return;
    let docValues;
    try {
      docValues = await docsValidatorRef.current();
    } catch {
      return;
    }

    const finalData = { ...formData, ...docValues };
    setSubmitting(true);

    try {
      const token = localStorage.getItem("authToken");
      const headers = { Authorization: `Bearer ${token}` };
      const primarySkillsFiltered = (finalData.primarySkills || []).filter(Boolean);

      const payload = {
        partnerOrganization,
        name: finalData.name,
        role: finalData.role,
        email: finalData.email,
        phone: finalData.phone,
        gender: finalData.gender || undefined,
        dob: finalData.dob || undefined,
        primarySkills: primarySkillsFiltered,
        secondarySkills: finalData.secondarySkills || [],
        skills: finalData.skills || [],
        experienceYears: parseInt(finalData.experienceYears) || 0,
        experienceMonths: parseInt(finalData.experienceMonths) || 0,
        rate: parseFloat(finalData.rate) || 0,
        notice: finalData.notice,
        location: finalData.location,
        country: finalData.country || undefined,
        availability: finalData.availability,
        summary: finalData.summary || "",
        university: finalData.university || undefined,
        graduationYear: finalData.graduationYear ? parseInt(finalData.graduationYear) : undefined,
        projects: finalData.projects || [],
      };

      if (finalData.resumeUrl) payload.resumeUrl = finalData.resumeUrl;
      if (finalData.aadhaarUrl) payload.aadhaarUrl = finalData.aadhaarUrl;
      if (finalData.passportUrl) payload.passportUrl = finalData.passportUrl;
      if (finalData.panUrl) payload.panUrl = finalData.panUrl;
      if (finalData.degreeUrl) payload.degreeUrl = finalData.degreeUrl;
      if (finalData.aadhaarNumber) payload.aadhaarNumber = finalData.aadhaarNumber;
      if (finalData.panNumber) payload.panNumber = finalData.panNumber;
      if (finalData.passportNumber) payload.passportNumber = finalData.passportNumber;

      if (isEditMode) {
        await axios.put(`${API_CONST.UPDATE_TALENT_PROFILE}/${editId}`, payload, { headers });
        message.success("Talent profile updated successfully!");
        navigate("/partner/bench-pool");
      } else {
        const res = await axios.post(API_CONST.ADD_TALENT_PROFILE, payload, { headers });
        message.success("Talent profile created successfully!");
        if (submitToJobId && res.data.profile?.id) {
          try {
            await axios.post(
              API_CONST.SUBMIT_PROFILES_TO_JOB,
              { jobId: submitToJobId, talentIds: [res.data.profile.id], partnerOrganization },
              { headers }
            );
            message.success("Profile submitted to job successfully!");
            navigate(`/partner/submit-profiles/${submitToJobId}`);
          } catch {
            message.warning("Profile created but failed to submit to job");
            navigate("/partner/bench-pool");
          }
        } else {
          navigate("/partner/bench-pool");
        }
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to save talent profile");
    } finally {
      setSubmitting(false);
    }
  };

  const stepContent = () => {
    switch (currentStep) {
      case 0:
        return <ResumeUpload onParsed={handleParsed} onSkip={handleSkip} />;
      case 1:
        return (
          <BasicDetails
            initialData={formData}
            onValidate={(fn) => { basicValidatorRef.current = fn; }}
          />
        );
      case 2:
        return (
          <ProfessionalDetails
            initialData={formData}
            onValidate={(fn) => { professionalValidatorRef.current = fn; }}
          />
        );
      case 3:
        return (
          <AvailabilityDocuments
            initialData={formData}
            onValidate={(fn) => { docsValidatorRef.current = fn; }}
            resumeFile={resumeFile}
            onPathsChange={(paths) => setFormData((prev) => ({ ...prev, ...paths }))}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <WizardWrapper>
        <div style={{ textAlign: "center" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
          <p style={{ marginTop: 16, color: "#64748b" }}>Loading talent profile...</p>
        </div>
      </WizardWrapper>
    );
  }

  const startStep = isEditMode ? 1 : 0;

  return (
    <WizardWrapper>
      <div className="wizard-card">
        {/* Header */}
        <div className="wizard-header">
          <div className="progress-bar">
            {STEPS.slice(startStep).map((step, idx) => {
              const stepIdx = idx + startStep;
              const isDone = stepIdx < currentStep;
              const isActive = stepIdx === currentStep;
              const stateClass = isActive ? "active" : isDone ? "completed" : "";
              return (
                <div key={step.key} className="progress-step">
                  <div className="step-node">
                    <div className={`step-dot ${stateClass}`}>{stepIdx + 1}</div>
                    <span className={`step-name ${stateClass}`}>{step.title}</span>
                  </div>
                  {idx < STEPS.length - startStep - 1 && (
                    <div className={`step-line ${isDone ? "completed" : ""}`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="step-info">
            <div className="step-label">
              Step {currentStep - startStep + 1} of {STEPS.length - startStep}
            </div>
            <h2 className="step-title">{STEPS[currentStep]?.title}</h2>
            <p className="step-description">{STEPS[currentStep]?.description}</p>
          </div>
        </div>

        {/* Body */}
        <div className="wizard-body">
          {stepContent()}
        </div>

        {/* Footer */}
        <div className="wizard-footer">
          <span className="step-counter">
            {isEditMode ? "Editing: " : ""}{partnerOrganization || ""}
          </span>
          <div className="nav-buttons">
            {currentStep === startStep ? (
              <WizardButton className="cancel-btn" onClick={handleCancel}>
                Cancel
              </WizardButton>
            ) : (
              <WizardButton className="back-btn" onClick={handleBack} disabled={submitting}>
                ← Back
              </WizardButton>
            )}

            {currentStep < 3 && currentStep > 0 && (
              <WizardButton className="next-btn" onClick={handleNext}>
                Continue →
              </WizardButton>
            )}

            {currentStep === 3 && (
              <WizardButton
                className="submit-btn"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting
                  ? <><Spin indicator={<LoadingOutlined style={{ color: "white" }} spin />} size="small" /> &nbsp;Saving...</>
                  : isEditMode ? "Save Changes" : "Create Profile"}
              </WizardButton>
            )}
          </div>
        </div>
      </div>
    </WizardWrapper>
  );
};

export default AddTalentProfile;
